import { SearchAdapter } from '@empathy/search-adapter';
import { container } from '../../../container';
import { DEPENDENCIES } from '../../../container/dependencies.const';
import { DiscoveryWallState } from '../../../models';
import { DISCOVERY_WALL_ACTIONS_KEYS, DISCOVERY_WALL_GETTERS_KEYS, DISCOVERY_WALL_MUTATIONS_KEYS } from '../discovery-wall.const';
import { DiscoveryWallAction, DiscoveryWallContext } from '../discovery-wall.interfaces';

type AdapterDiscoveryWallMethods =
  'getUserRecommendations'
  | 'getSectionRecommendations'
  | 'getClicksRecommendations'
  | 'getQueriesRecommendations';
type GetDiscoveryWallRecommendation<MethodName extends AdapterDiscoveryWallMethods>
  = (context: DiscoveryWallContext) => ReturnType<SearchAdapter[MethodName]> | Promise<{}>;

type DiscoveryWallGetters = keyof typeof DISCOVERY_WALL_GETTERS_KEYS;

export class GetDiscoveryWallRecommendationsAction implements DiscoveryWallAction {
  actions = {
    [DISCOVERY_WALL_ACTIONS_KEYS.getClicksRecommendations]:
      this.createRecommendationsAction('clicksRecommendationsRequest', 'getClicksRecommendations', 'clicksRecommendations'),
    [DISCOVERY_WALL_ACTIONS_KEYS.getQueriesRecommendations]:
      this.createRecommendationsAction('queriesRecommendationsRequest', 'getQueriesRecommendations', 'queriesRecommendations'),
    [DISCOVERY_WALL_ACTIONS_KEYS.getSectionRecommendations]:
      this.createRecommendationsAction('sectionRecommendationsRequest', 'getSectionRecommendations', 'sectionRecommendations'),
    [DISCOVERY_WALL_ACTIONS_KEYS.getUserRecommendations]:
      this.createRecommendationsAction('userRecommendationsRequest', 'getUserRecommendations', 'userRecommendations')
  };

  /**
   * Generates the code of the discovery wall actions
   * A DW action is in charge of requesting the data and storing it in the state.
   * @param requestGetter The getter with the request
   * @param adapterMethod The adapter method to be called
   * @param storeResponseAs The key of the state for storing the results
   */
  protected createRecommendationsAction<MethodName extends AdapterDiscoveryWallMethods>
  (requestGetter: DiscoveryWallGetters, adapterMethod: MethodName, storeResponseAs: keyof Partial<DiscoveryWallState>)
    : GetDiscoveryWallRecommendation<MethodName> {
    return ({ getters, commit }: DiscoveryWallContext) => {
      const request = getters[DISCOVERY_WALL_GETTERS_KEYS[requestGetter]];
      const adapter = container.get<SearchAdapter>(DEPENDENCIES.Adapter);
      return request
        ? adapter[adapterMethod](request)
          .then(response => {
            commit(DISCOVERY_WALL_MUTATIONS_KEYS.setDiscoveryWallState, { [storeResponseAs]: response });
            return response;
          })
        : Promise.resolve({});
    };
  }
}
